import { useEffect, useMemo } from "react";
import { X, PlayCircle } from "lucide-react";
import { useBusinessInfo } from "@/contexts/BusinessInfoContext";
import { toYouTubeEmbed } from "@/lib/youtube";
import { trackTutorialClick } from "@/lib/analytics";

/**
 * Compact "Watch tutorial" button that opens the configured YouTube tutorial
 * in a full-screen lightbox. Renders nothing when no tutorial URL is set —
 * safe to drop into any funnel step without a config-fallback branch.
 *
 * Autoplays the video once the modal opens so the user doesn't have to
 * hunt for the play button. ESC closes.
 */
export function TutorialLightbox({ open, onClose }) {
  const { info } = useBusinessInfo();
  const embedUrl = useMemo(() => {
    const base = toYouTubeEmbed(info?.tutorial_video_url);
    return base ? `${base}?autoplay=1&rel=0` : null;
  }, [info?.tutorial_video_url]);

  // ESC to close + lock body scroll while the lightbox is up.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !embedUrl) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="tutorial-lightbox"
    >
      <div
        className="relative w-full max-w-4xl bg-black border-2 border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="tutorial-lightbox-close"
          aria-label="Close tutorial"
          className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-white text-[#0A0A0A] flex items-center justify-center hover:bg-[#059669] hover:text-white transition-colors border-2 border-[#0A0A0A]"
        >
          <X className="w-5 h-5" strokeWidth={2.5} />
        </button>
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={embedUrl}
            title="How CTP Print Online works — tutorial"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="tutorial-lightbox-iframe"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * "Watch tutorial" button that pairs with <TutorialLightbox>. Hides itself
 * automatically when no tutorial URL is configured — so first-launch users
 * without a video set never see a dead button.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <WatchTutorialButton onClick={() => setOpen(true)} />
 *   <TutorialLightbox open={open} onClose={() => setOpen(false)} />
 */
export function WatchTutorialButton({ onClick, className = "", testId = "watch-tutorial-btn" }) {
  const { info } = useBusinessInfo();
  const hasVideo = !!toYouTubeEmbed(info?.tutorial_video_url);
  if (!hasVideo) return null;
  return (
    <button
      onClick={() => { trackTutorialClick(); onClick?.(); }}
      data-testid={testId}
      className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-[#059669] hover:text-[#0A0A0A] transition-colors ${className}`}
    >
      <PlayCircle className="w-4 h-4" strokeWidth={2.5} />
      Watch tutorial
    </button>
  );
}
